import { useCallback, useEffect, useRef, useState } from "react";
import { CO_CAU_HINH } from "../lib/firebase.js";
import {
  emailDangCho,
  emailHopLe,
  guiLienKet,
  hoanTatDangNhap,
  laLienKetDangNhap,
  theoDoiDangNhap,
  dangXuat,
  xoaTaiKhoan,
  phienConTuoi,
  dangNhapGoogle,
  ketQuaChuyenHuong,
} from "../lib/auth.js";
import { dongBo, xoaTrenTaiKhoan, coBytes, TRAN_BYTE } from "../lib/sync.js";
import {
  readWorkspace,
  writeWorkspace,
  datTaiKhoan,
  docKhoKhach,
  xoaKhoKhach,
  lamMoiBoNhoTam,
} from "../lib/workspace.js";
import { coNoiDung, gopWorkspace } from "@shared/gopWorkspace.js";

/**
 * Khối tài khoản trong Bản chuẩn bị.
 *
 * Nguyên tắc: đây là TIỆN ÍCH THÊM, không phải cửa vào. Chưa đăng nhập thì mọi
 * thứ phía dưới vẫn chạy đủ. Chưa cấu hình Firebase thì khối này không hiện,
 * để không có nút bấm vào không ra gì.
 */
export default function TaiKhoan() {
  const [dungLuong, setDungLuong] = useState(null);
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [trangThai, setTrangThai] = useState("");
  const [ban, setBan] = useState(false);
  const [hoiEmail, setHoiEmail] = useState(false);
  const [coBanKhach, setCoBanKhach] = useState(false);
  // Mốc capNhat của lần đồng bộ gần nhất. So với mốc hiện tại để biết còn thay
  // đổi nào chưa lên tài khoản — người dùng rất dễ nghĩ "đã đăng nhập là đã
  // lưu lên tài khoản", trong khi thực tế phải bấm đồng bộ.
  const [mocDaDongBo, setMocDaDongBo] = useState(null);
  const [mocHienTai, setMocHienTai] = useState(null);
  const daDongBo = useRef(null);

  const dongBoNgay = useCallback(async (u, tuDong) => {
    if (!u) return;
    setBan(true);
    setTrangThai("Đang đồng bộ…");
    try {
      const gop = await dongBo(u.uid, readWorkspace(), u.email);
      writeWorkspace(gop);
      // Bộ nhớ tạm của form vẫn giữ giá trị cũ sau khi nhận dữ liệu đồng bộ;
      // không xoá thì hồ sơ đã cập nhật mà mở công cụ ra vẫn thấy số cũ.
      lamMoiBoNhoTam();
      setMocDaDongBo(readWorkspace().capNhat || null);
      setDungLuong(Math.round(coBytes(gop) / 1024));
      setTrangThai(
        `Đã đồng bộ với tài khoản lúc ${new Date().toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })}.`,
      );
    } catch (e) {
      setTrangThai(
        tuDong
          ? `Chưa đồng bộ được: ${e.message} Dữ liệu trên thiết bị vẫn nguyên vẹn.`
          : `Chưa đồng bộ được: ${e.message}`,
      );
    } finally {
      setBan(false);
    }
  }, []);

  /**
   * Mang dữ liệu của phiên khách vào tài khoản. CHỈ chạy khi người dùng bấm.
   * Tự động làm việc này chính là lỗi cũ: máy dùng chung thì dữ liệu người
   * trước chảy sang tài khoản người sau.
   */
  async function nhapBanKhach() {
    const khach = docKhoKhach();
    setBan(true);
    try {
      const gop = gopWorkspace(readWorkspace(), khach);
      writeWorkspace(gop);
      await dongBo(user.uid, gop, user.email);
      xoaKhoKhach();
      setCoBanKhach(false);
      setTrangThai("Đã đưa dữ liệu trên máy này vào tài khoản.");
    } catch (e) {
      setTrangThai("Chưa đưa được: " + e.message);
    } finally {
      setBan(false);
    }
  }

  // Hoàn tất đăng nhập nếu người dùng vừa bấm liên kết trong hộp thư.
  useEffect(() => {
    if (!CO_CAU_HINH) return;
    let huy = false;
    (async () => {
      try {
        if (!(await laLienKetDangNhap())) return;
        const dc = emailDangCho();
        if (!dc) {
          // Mở link trên thiết bị khác với thiết bị đã bấm gửi: phải hỏi lại.
          if (!huy) {
            setHoiEmail(true);
            setTrangThai("Nhập lại email bạn đã dùng để nhận liên kết này.");
          }
          return;
        }
        await hoanTatDangNhap(dc);
      } catch (e) {
        if (!huy) setTrangThai(e.message);
      }
    })();
    return () => {
      huy = true;
    };
  }, []);

  // Quay lại từ luồng chuyển hướng của Google (trình duyệt chặn cửa sổ bật lên).
  useEffect(() => {
    if (!CO_CAU_HINH) return;
    ketQuaChuyenHuong().catch(() => {});
  }, []);

  useEffect(() => theoDoiDangNhap(setUser), []);

  // Theo dõi mọi thay đổi trong Bản chuẩn bị để cập nhật trạng thái đồng bộ.
  useEffect(() => {
    const doc = () => setMocHienTai(readWorkspace().capNhat || null);
    doc();
    window.addEventListener("workspace-change", doc);
    return () => window.removeEventListener("workspace-change", doc);
  }, []);

  // Đổi kho dữ liệu theo người đang đăng nhập, rồi mới đồng bộ.
  //
  // Thứ tự quan trọng: datTaiKhoan() phải chạy TRƯỚC dongBoNgay(), nếu không
  // readWorkspace() bên trong vẫn đọc kho của người cũ và đẩy nhầm lên tài
  // khoản mới. Đó chính là lỗi đã sửa.
  useEffect(() => {
    datTaiKhoan(user?.uid || null);
    if (!user) {
      daDongBo.current = null;
      setCoBanKhach(false);
      return;
    }
    // Phiên khách có dữ liệu thì hỏi, không tự nuốt.
    setCoBanKhach(coNoiDung(docKhoKhach()));
    if (daDongBo.current === user.uid) return;
    daDongBo.current = user.uid;
    dongBoNgay(user, true);
  }, [user, dongBoNgay]);

  if (!CO_CAU_HINH) return null;

  async function gui() {
    setBan(true);
    try {
      await guiLienKet(email);
      setTrangThai(
        `Đã gửi liên kết tới ${email.trim()}. Mở hộp thư và bấm vào liên kết để đăng nhập. ` +
          "Không thấy thì kiểm tra mục spam.",
      );
    } catch (e) {
      setTrangThai(e.message);
    } finally {
      setBan(false);
    }
  }

  async function vaoBangGoogle() {
    setBan(true);
    setTrangThai("");
    try {
      await dangNhapGoogle();
    } catch (e) {
      setTrangThai(e.message);
    } finally {
      setBan(false);
    }
  }

  async function xacNhanLai() {
    setBan(true);
    try {
      await hoanTatDangNhap(email);
      setHoiEmail(false);
      setTrangThai("");
    } catch (e) {
      setTrangThai(e.message);
    } finally {
      setBan(false);
    }
  }

  async function xoaHet() {
    if (
      !confirm(
        "Xoá tài khoản và toàn bộ dữ liệu đã đồng bộ trên máy chủ? " +
          "Bản trên thiết bị này vẫn giữ nguyên. Hành động này không hoàn tác được.",
      )
    )
      return;
    setBan(true);
    try {
      // Kiểm tra phiên còn tươi TRƯỚC KHI xoá bất cứ thứ gì.
      //
      // Thứ tự cũ xoá Firestore trước rồi mới xoá tài khoản Auth. Nếu Firebase
      // từ chối bước sau vì phiên đăng nhập đã cũ, người dùng rơi vào trạng
      // thái tệ nhất: dữ liệu trên máy chủ mất rồi mà tài khoản vẫn còn. Không
      // hoàn tác được.
      if (!(await phienConTuoi())) {
        setTrangThai(
          "Vì lý do an toàn, hãy đăng xuất rồi đăng nhập lại, sau đó xoá ngay. "
            + "Chưa có gì bị xoá cả.",
        );
        setBan(false);
        return;
      }
      // Xoá dữ liệu trước, rồi mới xoá tài khoản: xoá tài khoản trước là mất
      // quyền ghi và document nằm lại vĩnh viễn.
      await xoaTrenTaiKhoan(user.uid);
      await xoaTaiKhoan();
      setTrangThai(
        "Đã xoá tài khoản và dữ liệu trên máy chủ. Bản trên thiết bị này vẫn còn.",
      );
    } catch (e) {
      setTrangThai(e.message);
    } finally {
      setBan(false);
    }
  }

  const chuaDongBo = Boolean(user && mocHienTai && mocHienTai !== mocDaDongBo);
  const data = typeof window === "undefined" ? {} : readWorkspace();
  const nhanTin = Boolean(data.profile?.nhanTinCapNhat);

  return (
    <section className="surface section-space" id="tai-khoan">
      <div className="section-bar">
        <h2>Đồng bộ theo tài khoản</h2>
        <span className="pill">Tuỳ chọn</span>
      </div>

      {user ? (
        <>
          <p className="muted">
            Đang đăng nhập bằng <strong>{user.email}</strong>
            {user.providerData?.[0]?.providerId === "google.com"
              ? " (tài khoản Google)"
              : ""}
            . Bản chuẩn bị được
            gộp giữa các thiết bị: mỗi công cụ giữ bản sửa sau cùng, không bên
            nào ghi đè cả cục lên bên kia.
          </p>
          <label className="task-row">
            <input
              type="checkbox"
              checked={nhanTin}
              onChange={(e) => {
                const d = readWorkspace();
                writeWorkspace({
                  ...d,
                  profile: { ...(d.profile || {}), nhanTinCapNhat: e.target.checked },
                });
              }}
            />
            <span>
              Nhận email khi có công cụ mới hoặc cập nhật danh mục nguồn vốn.
              Không quảng cáo, bỏ đăng ký bất cứ lúc nào.
            </span>
          </label>
          {coBanKhach && (
            <div className="notice">
              <p>
                Trình duyệt này còn một bản chuẩn bị chưa gắn với tài khoản nào.
                Nếu đó là của bạn, đưa vào tài khoản. Nếu máy dùng chung và đó
                là của người khác, đừng đưa vào.
              </p>
              <div className="hang-nut">
                <button className="nut phu" disabled={ban} onClick={nhapBanKhach}>
                  Đưa vào tài khoản của tôi
                </button>
                <button
                  className="nut phu"
                  disabled={ban}
                  onClick={() => setCoBanKhach(false)}
                >
                  Không phải của tôi
                </button>
              </div>
            </div>
          )}
          <p className={chuaDongBo ? "trang-thai-cho" : "trang-thai-xong"}>
            {chuaDongBo
              ? "Đã lưu trên máy này, còn thay đổi chưa đưa lên tài khoản"
              : "Đã đồng bộ, máy này và tài khoản đang khớp nhau"}
          </p>
          <div className="hang-nut">
            <button className="nut" disabled={ban} onClick={() => dongBoNgay(user)}>
              {chuaDongBo ? "Đồng bộ thay đổi" : "Đồng bộ ngay"}
            </button>
            <button
              className="nut phu"
              disabled={ban}
              onClick={async () => {
                await dangXuat();
                setTrangThai("Đã đăng xuất. Dữ liệu trên thiết bị này vẫn còn.");
              }}
            >
              Đăng xuất
            </button>
          </div>
          {/* Hành động không hoàn tác được: tách khỏi nhóm lưu/đồng bộ thường dùng. */}
          <details className="ws-quan-ly-tk">
            <summary>Quản lý tài khoản</summary>
            {dungLuong != null && (
              <p className="nho muted">
                Dung lượng đã đồng bộ: {dungLuong} KB / {Math.round(TRAN_BYTE / 1024)} KB.
              </p>
            )}
            <p className="nho muted">
              Xoá tài khoản sẽ xoá email và mọi dữ liệu đã đồng bộ trên máy chủ. Dữ liệu trên trình duyệt này vẫn còn.
            </p>
            <button className="nut phu danger" disabled={ban} onClick={xoaHet}>
              Xoá tài khoản và dữ liệu
            </button>
          </details>
        </>
      ) : (
        <>
          <p className="muted">
            Chỉ dùng để mở lại Bản chuẩn bị trên máy khác. Không cần mật khẩu:
            nhập email, bấm liên kết trong hộp thư là xong. Không đăng nhập thì
            mọi công cụ vẫn dùng đủ như hiện nay.
          </p>
          <div className="o-nhap">
            <div>
              <label htmlFor="email-dang-nhap">Email</label>
              <input
                id="email-dang-nhap"
                type="email"
                autoComplete="email"
                placeholder="ban@congty.vn"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          <div className="hang-nut">
            <button
              className="nut"
              disabled={ban || !emailHopLe(email)}
              onClick={hoiEmail ? xacNhanLai : gui}
            >
              {hoiEmail ? "Xác nhận email" : "Gửi liên kết đăng nhập"}
            </button>
          </div>
          <p className="muted ngan-cach">hoặc</p>
          <div className="hang-nut">
            <button className="nut phu" disabled={ban} onClick={vaoBangGoogle}>
              Đăng nhập bằng tài khoản Google
            </button>
          </div>
          <p className="nho muted">
            Chúng tôi lưu email và Bản chuẩn bị của bạn để phục vụ việc đồng bộ.
            Xem <a href="/quyen-rieng-tu">Chính sách quyền riêng tư</a>.
          </p>
        </>
      )}

      <p role="status">{trangThai}</p>
    </section>
  );
}
